import React, { Component } from 'react';
import { FormContainer, FormService } from '@tencent/wb-ext-form';
import i18n from '@i18next/i18n';
import { FormPanelName } from '@src/framework/workbench/drawer/constants';
import { TdwDrawerConstMap, tdwDrawerReport } from '@src/framework/workbench/drawer/report';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import styles from './index.less';
import Close from '@com/animation-editor/images/close.svg';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IReactionDisposer, reaction } from 'mobx';
import { isVideoElement } from '@src/slide/domain/ui-model/elements/element-judge';
import { InjectorService } from '@tencent/workbench';
import FormSchemaLogic from '@src/slide/application/service/property-editor-manager/form-schema-logic';

const LabelReportMap = {
    [i18n.t('样式')]: 'Style',
    [i18n.t('文字')]: 'Text',
    [i18n.t('布局')]: 'Layout',
    [i18n.t('图片')]: 'Picture',
    [i18n.t('表格')]: 'Table',
    [i18n.t('背景')]: 'Background',
};

export const TitleMap = {
    [UIModelType.SHAPE]: i18n.t('形状'),
    [UIModelType.GROUP]: i18n.t('形状'),
    [UIModelType.MULTIPLY]: i18n.t('形状'),
    [UIModelType.IMAGE]: i18n.t('图片'),
    [UIModelType.EMPTY]: i18n.t('背景'),
    [UIModelType.CONNECTOR]: i18n.t('形状'),
    [UIModelType.TABLE]: i18n.t('形状'),
    [UIModelType.NOTE]: i18n.t('背景'),
    [UIModelType.CHART]: i18n.t('图表'),
    [UIModelType.SMART_ART]: i18n.t('形状'),
};

interface IState {
    title: string;
    headless: boolean;
    schemaChange: boolean;
}

interface IProps {
    close(): void;
}

const SCHEMA_CHANGE_TIME = 100;

export default class FormContent extends Component<IProps, IState> {
    private formService: FormService;
    private lastSchemaLength: number;
    private timer: any;
    private selectedModelReaction: IReactionDisposer;

    constructor(props) {
        super(props);
        this.state = {
            title: this.getEditorTitle(),
            headless: this.isHeadlessMode(),
            schemaChange: false,
        };
        this.formService = InjectorService.get(FormService);
        this.lastSchemaLength = this.getSchemaLength();
    }

    public componentDidMount() {
        // 监听选中元素
        this.selectedModelReaction = reaction(
            () => ({
                selectedModel: this.getAppServices().viewableServiceLayer.selectionService.getSelectedModel(),
            }),
            () => {
                if (this.timer) {
                    clearTimeout(this.timer);
                }
                const schemaLength = this.getSchemaLength();
                const schemaChange = schemaLength !== this.lastSchemaLength;
                this.setState({
                    title: this.getEditorTitle(),
                    headless: this.isHeadlessMode(),
                    schemaChange,
                });
                if (schemaChange) {
                    this.changeTransition();
                    this.timer = setTimeout(() => {
                        this.setState({
                            schemaChange: false,
                        });
                        this.lastSchemaLength = schemaLength;
                    }, SCHEMA_CHANGE_TIME);
                }
            },
        );
    }

    public componentWillUnmount() {
        this.selectedModelReaction?.();
        if (this.timer) {
            clearTimeout(this.timer);
        }
    }

    public render() {
        const { title, headless, schemaChange } = this.state;
        const height = headless ? '100%' : 'calc(100% - 56px)';
        return (
            <React.Fragment>
                <div style={{ height: '100%', width: '100%', position: 'absolute' }}>
                    <div className={styles['property-editor-header']}>
                        <div
                            className={styles['property-editor-header-title']}>{i18n.t('设置{{0}}格式', { 0: title })}</div>
                        <img className={styles['property-editor-header-close-button']} src={Close}
                            onClick={() => this.props?.close()}/>
                    </div>
                    <div style={{ height, display: schemaChange ? 'none' : 'block' }} >
                        <FormContainer
                            forms={[FormPanelName.SHAPE, FormPanelName.TEXT]}
                            onFormChange={this.handleFormChange}
                            onTabChange={this.handleTabChange}
                            headless={headless}
                            tabBarSize='small'
                            indicatorWidth='small'
                            accordionStyle={{ border: 'none' }}
                        />
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private handleFormChange = (id) => {
        const { editableServiceLayer } = this.getAppServices();
        if (!editableServiceLayer) return;

        this.formService.changeForm(id, 0);
    };

    private getEditorTitle = () => {
        const model = this.getAppServices().viewableServiceLayer.selectionService.getSelectedModel();
        if (!model) {
            return TitleMap[UIModelType.EMPTY];
        }

        if (isVideoElement(model)) {
            return i18n.t('视频');
        }
        return TitleMap[model.type];
    };

    private isHeadlessMode = () => {
        const schemaLength = this.getSchemaLength();
        return schemaLength <= 1;
    };

    private getSchemaLength = (): number => {
        const model = this.getAppServices().viewableServiceLayer.selectionService.getSelectedModel();
        const schema = FormSchemaLogic.getSelectFormSchema(model);

        return schema && Object.keys(schema).length;
    };

    private handleTabChange = (tab: string, label: string) => {
        this.changeTransition(false);

        if (LabelReportMap[label]) {
            tdwDrawerReport(TdwDrawerConstMap.tabChange, { ver5: LabelReportMap[label] });
        }
    };

    private changeTransition = (add = true) => {
        const tabsIndicator = document.querySelector('[class^="form_container"] .dui-tabs-indicator');
        const className = styles['no-transition'];
        if (add) {
            tabsIndicator?.classList.add(className);
        } else {
            tabsIndicator?.classList.remove(className);
        }
    };

    private getAppServices(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IPcEditorAppServices;
    }
}
